import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../ui/Modal.tsx';
import { useTranslation } from '../../i18n.ts';
import { useTierAccess } from '../../hooks/useTierAccess.ts';
import { useSessionStore } from '../../stores/useSessionStore.ts';
import { useProjectStore } from '../../stores/useProjectStore.ts';
import { TIER_CONFIG } from '../../tierConfig.ts';

/**
 * #1857 (audit B8) — één keer na het einde van de proef: wat blijft (alles), wat er
 * voor dít account verandert, en een keuze. Daarvoor verdween de proefbadge zonder een
 * woord en merkte iemand het pas aan een weigering midden in het werk.
 *
 * "Gezien" per account en per einddatum in localStorage: een gemak per browser. Komt
 * het venster op een tweede apparaat nog eens, dan is dat geen schade.
 */
const VENSTER_DAGEN = 30;

const ProefAfscheid: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSessionStore((s) => s.user) as unknown as Record<string, unknown> | null;
  const projects = useProjectStore((s) => s.projects);
  const { isFree, hasSubscription, trialEndsAt } = useTierAccess();
  const [open, setOpen] = useState(false);

  const sleutel =
    user?.id && trialEndsAt ? `pt_proef_afscheid_${user.id}_${trialEndsAt.toISOString()}` : null;

  useEffect(() => {
    if (!sleutel || !trialEndsAt || !isFree || hasSubscription) return;
    const verlopen = Date.now() - trialEndsAt.getTime();
    if (verlopen <= 0 || verlopen > VENSTER_DAGEN * 24 * 3600 * 1000) return;
    try {
      if (localStorage.getItem(sleutel)) return;
    } catch {
      // Geen opslag beschikbaar: liever één keer te veel tonen dan nooit.
    }
    setOpen(true);
  }, [sleutel, trialEndsAt, isFree, hasSubscription]);

  const eigen = useMemo(
    () => projects.filter((p) => !p.owner_account_id || p.owner_account_id === user?.id),
    [projects, user?.id]
  );
  const grens = TIER_CONFIG.free.maxPeople;
  const vol = eigen.filter((p) => (p.person_count || 0) >= grens);

  const sluit = () => {
    setOpen(false);
    try {
      if (sleutel) localStorage.setItem(sleutel, '1');
    } catch {
      // zie boven
    }
  };

  if (!open) return null;

  return (
    <Modal isOpen onClose={sluit} titleId="proef-afscheid-titel" size="md">
      <div className="p-6 md:p-10 space-y-6" data-testid="proef-afscheid">
        <div className="space-y-2">
          <h2
            id="proef-afscheid-titel"
            className="text-2xl font-serif font-bold text-pt-forest dark:text-[var(--text-main)]"
          >
            {t('proefAfscheidTitel')}
          </h2>
          <p className="text-sm text-pt-forest dark:text-[var(--text-main)]">
            {t('proefAfscheidIntro')}
          </p>
        </div>

        <section className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-widest text-pt-sage-text dark:text-[var(--text-muted)]">
            {t('proefAfscheidBlijftKop')}
          </h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-pt-forest dark:text-[var(--text-main)]">
            <li>{t('proefAfscheidBlijft1')}</li>
            <li>{t('proefAfscheidBlijft2')}</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-widest text-pt-sage-text dark:text-[var(--text-muted)]">
            {t('proefAfscheidAndersKop')}
          </h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-pt-forest dark:text-[var(--text-main)]">
            {eigen.length > 1 && (
              <li>{t('proefAfscheidProjecten', { aantal: String(eigen.length) })}</li>
            )}
            {vol.map((p) => (
              <li key={p.id}>
                {t('proefAfscheidVolProject', {
                  naam: p.name,
                  aantal: String(p.person_count),
                  grens: String(grens),
                })}
              </li>
            ))}
            {eigen.length <= 1 && vol.length === 0 && (
              <li>{t('proefAfscheidGrens', { grens: String(grens) })}</li>
            )}
            <li>{t('proefAfscheidArchief')}</li>
          </ul>
        </section>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="button"
            onClick={() => {
              sluit();
              navigate('/account?tab=billing');
            }}
            className="flex-1 py-3 rounded-xl bg-pt-gold text-pt-forest font-bold text-sm hover:brightness-105 transition"
          >
            {t('proefAfscheidKies')}
          </button>
          <button
            type="button"
            onClick={sluit}
            className="flex-1 py-3 rounded-xl border border-pt-forest/20 text-pt-forest dark:text-[var(--text-main)] font-bold text-sm hover:bg-pt-forest/5 transition"
          >
            {t('proefAfscheidGratis')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ProefAfscheid;
